import React, { useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../context/I18nContext';
import './RentReportingBlueprint.css';
import {
  Home,
  CheckCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  ExternalLink,
  Calculator,
  FileText,
  DollarSign,
  Calendar,
  Shield,
  TrendingUp,
  Users,
  Building2,
  Star,
  Clock,
  Check,
  X,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// ===================== ADMIN-EDITABLE PROVIDER DATA =====================
// These values should be managed by admin panel in production
const RENT_REPORTING_PROVIDERS = [
  {
    id: 'boom',
    provider_name: 'Boom',
    logo_placeholder: 'B',
    bureaus_reported: ['Experian', 'Equifax', 'TransUnion'],
    past_rent_reporting_limit_months: 24,
    reports_future_rent: true,
    setup_fee: 10,
    monthly_fee: 5,
    past_rent_reporting_fee: 25,
    landlord_required: 'varies',
    lease_verification_required: true,
    payment_proof_required: true,
    negative_reporting_risk: 'check_provider_policy',
    best_for: 'Renters who want broad bureau coverage and up to 24 months of past rent reporting',
    watch_outs: 'Verify current pricing, eligibility, and reporting policy before enrolling.',
    website_url: 'https://www.boompay.app/',
    admin_editable: true,
    provider_note: 'Boom states that it reports to all three bureaus and can report up to 24 months of past rent payments from the current residential lease. Pricing shown is illustrative—verify current terms on their website.',
  },
  {
    id: 'rentreporters',
    provider_name: 'RentReporters',
    logo_placeholder: 'RR',
    bureaus_reported: ['Experian', 'Equifax', 'TransUnion'],
    past_rent_reporting_limit_months: 24,
    reports_future_rent: true,
    setup_fee: 0,
    monthly_fee: 9.95,
    past_rent_reporting_fee: 50,
    landlord_required: 'yes',
    lease_verification_required: true,
    payment_proof_required: true,
    negative_reporting_risk: 'check_provider_policy',
    best_for: 'Renters with cooperative landlords who can verify rent payment history',
    watch_outs: 'Landlord/property manager verification is typically required. Confirm current pricing and VIP processing terms.',
    website_url: 'https://www.rentreporters.com/',
    admin_editable: true,
    provider_note: 'RentReporters reports rent payment history to bureaus as a rental tradeline. They offer past rent reporting through VIP processing. Pricing and terms should be verified on their website.',
  },
  {
    id: 'self_rent',
    provider_name: 'Self Rent Reporting',
    logo_placeholder: 'SR',
    bureaus_reported: ['Experian', 'Equifax', 'TransUnion'],
    past_rent_reporting_limit_months: 24,
    reports_future_rent: true,
    setup_fee: 0,
    monthly_fee: 6.95,
    past_rent_reporting_fee: 50,
    landlord_required: 'no',
    lease_verification_required: true,
    payment_proof_required: true,
    negative_reporting_risk: 'check_provider_policy',
    best_for: 'Renters who want to report without landlord involvement, using bank/payment verification',
    watch_outs: 'Verify current pricing and supported verification methods. Payment proof via linked account may be required.',
    website_url: 'https://www.self.inc/rent-reporting',
    admin_editable: true,
    provider_note: 'Self\'s rent reporting can report rent to bureaus and past rent up to 24 months for a one-time fee. Exact current pricing must be verified on their website.',
  },
];

// ===================== CALCULATOR LOGIC =====================
const calculateRentReportingFit = (inputs) => {
  const {
    monthlyRent,
    monthsOfPastRent,
    providerMaxMonths,
    setupFee,
    monthlyFee,
    pastRentFee,
    numBureaus,
    hasPaymentProof,
    hasLandlordVerification,
  } = inputs;

  const eligiblePastMonths = Math.min(monthsOfPastRent, providerMaxMonths);
  const potentialReportedRent = monthlyRent * eligiblePastMonths;
  const firstYearCost = setupFee + pastRentFee + (monthlyFee * 12);
  const costPerReportedMonth = eligiblePastMonths > 0 ? (firstYearCost / eligiblePastMonths) : firstYearCost;
  
  // Bureau coverage strength
  let bureauStrength = 'weak';
  if (numBureaus >= 3) bureauStrength = 'strong';
  else if (numBureaus >= 2) bureauStrength = 'moderate';

  // Readiness assessment
  let readinessScore = 0;
  let readinessIssues = [];

  if (hasPaymentProof) readinessScore += 30;
  else readinessIssues.push('paymentProof');

  if (hasLandlordVerification) readinessScore += 25;
  else readinessIssues.push('landlordVerify');

  if (eligiblePastMonths >= 12) readinessScore += 20;
  else if (eligiblePastMonths >= 6) readinessScore += 10;
  else readinessIssues.push('limitedPast');

  if (numBureaus >= 3) readinessScore += 15;
  else if (numBureaus >= 2) readinessScore += 10;
  else readinessIssues.push('singleBureau');

  if (costPerReportedMonth < 10) readinessScore += 10;
  else if (costPerReportedMonth < 20) readinessScore += 5;

  let fitResult = 'low';
  let fitDescKey = 'lowDesc';

  if (readinessScore >= 70) {
    fitResult = 'high';
    fitDescKey = 'highDesc';
  } else if (readinessScore >= 45) {
    fitResult = 'medium';
    fitDescKey = 'mediumDesc';
  }

  return {
    eligiblePastMonths,
    potentialReportedRent,
    firstYearCost,
    costPerReportedMonth: Math.round(costPerReportedMonth * 100) / 100,
    bureauStrength,
    readinessScore,
    readinessIssues,
    fitResult,
    fitDescKey,
  };
};

// ===================== HELPER COMPONENTS =====================
const BureauBadge = ({ bureau }) => {
  const colors = {
    'TransUnion': 'rr-badge-tu',
    'Equifax': 'rr-badge-eq',
    'Experian': 'rr-badge-ex',
  };
  return <span className={`rr-bureau-badge ${colors[bureau] || ''}`}>{bureau}</span>;
};

const VerificationBadge = ({ value, t, RR }) => {
  if (value === 'yes') {
    return <span className="rr-verify-badge required">{t(`${RR}.labels.verification.required`)}</span>;
  }
  if (value === 'no') {
    return <span className="rr-verify-badge not-required">{t(`${RR}.labels.verification.notRequired`)}</span>;
  }
  return <span className="rr-verify-badge varies">{t(`${RR}.labels.verification.varies`)}</span>;
};

const FitBadge = ({ result, t, RR }) => {
  const classes = {
    high: 'rr-fit-badge high',
    medium: 'rr-fit-badge medium',
    low: 'rr-fit-badge low',
  };
  return (
    <span className={classes[result] || classes.low}>
      {t(`${RR}.calculator.fit.${result}`)}
    </span>
  );
};

// ===================== MAIN COMPONENT =====================
const TAB_KEYS = ['overview', 'providers', 'calculator', 'checklist'];

const RentReportingBlueprint = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  const RR = 'academy.rentReportingBlueprint';
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  // Calculator state
  const [calcInputs, setCalcInputs] = useState({
    monthlyRent: 1500,
    monthsOfPastRent: 18,
    providerMaxMonths: 24,
    setupFee: 10,
    monthlyFee: 5,
    pastRentFee: 25,
    numBureaus: 3,
    hasPaymentProof: true,
    hasLandlordVerification: false,
  });

  const calcResults = useMemo(() => calculateRentReportingFit(calcInputs), [calcInputs]);

  const handleInputChange = (field, value) => {
    setCalcInputs(prev => ({
      ...prev,
      [field]: typeof value === 'boolean' ? value : parseFloat(value) || 0,
    }));
  };

  const selectProviderForCalc = (provider) => {
    setCalcInputs(prev => ({
      ...prev,
      providerMaxMonths: provider.past_rent_reporting_limit_months,
      setupFee: provider.setup_fee,
      monthlyFee: provider.monthly_fee,
      pastRentFee: provider.past_rent_reporting_fee,
      numBureaus: provider.bureaus_reported.length,
    }));
    setSelectedProvider(provider);
  };

  return (
    <div
      className={`rr-blueprint rr-blueprint-page${isDark ? ' dark' : ''}`}
      data-testid="rent-reporting-blueprint"
    >
      {/* Hero Section */}
      <div className="rr-hero">
        <div className="rr-hero-content">
          <div className="rr-hero-icon">
            <Home size={32} />
          </div>
          <div className="rr-hero-text">
            <h1 className="rr-hero-title">{t(`${RR}.hero.title`)}</h1>
            <p className="rr-hero-subtitle">{t(`${RR}.hero.subtitle`)}</p>
          </div>
        </div>
        <div className="rr-hero-stats">
          <div className="rr-stat-chip">
            <Building2 size={16} />
            <span>{t(`${RR}.hero.statProviders`)}</span>
          </div>
          <div className="rr-stat-chip">
            <Calendar size={16} />
            <span>{t(`${RR}.hero.statPastRent`)}</span>
          </div>
          <div className="rr-stat-chip">
            <Shield size={16} />
            <span>{t(`${RR}.hero.statBureaus`)}</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="rr-tabs">
        {TAB_KEYS.map((tabKey) => {
          const TabIcon = { overview: Info, providers: Building2, calculator: Calculator, checklist: CheckCircle }[tabKey];
          return (
            <button
              key={tabKey}
              className={`rr-tab ${activeTab === tabKey ? 'active' : ''}`}
              onClick={() => setActiveTab(tabKey)}
            >
              <TabIcon size={16} /> {t(`${RR}.tabs.${tabKey}`)}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="rr-tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="rr-overview">
            <div className="rr-section">
              <h2 className="rr-section-title">{t(`${RR}.overview.whatIsTitle`)}</h2>
              <p className="rr-section-text">{t(`${RR}.overview.whatIsText`)}</p>
            </div>

            <div className="rr-info-cards">
              <div className="rr-info-card glass">
                <div className="rr-info-icon emerald">
                  <TrendingUp size={24} />
                </div>
                <h3>{t(`${RR}.overview.benefitsTitle`)}</h3>
                <ul className="rr-info-list">
                  {['paymentHistory', 'thinFiles', 'majorBureaus', 'pastRent'].map((key) => (
                    <li key={key}>{t(`${RR}.overview.benefits.${key}`)}</li>
                  ))}
                </ul>
              </div>

              <div className="rr-info-card glass">
                <div className="rr-info-icon amber">
                  <AlertTriangle size={24} />
                </div>
                <h3>{t(`${RR}.overview.considerationsTitle`)}</h3>
                <ul className="rr-info-list">
                  {['scoringModels', 'latePayments', 'monthlyFees', 'landlordVerify'].map((key) => (
                    <li key={key}>{t(`${RR}.overview.considerations.${key}`)}</li>
                  ))}
                </ul>
              </div>

              <div className="rr-info-card glass">
                <div className="rr-info-icon slate">
                  <FileText size={24} />
                </div>
                <h3>{t(`${RR}.overview.requirementsTitle`)}</h3>
                <ul className="rr-info-list">
                  {['lease', 'proof', 'bankRecords', 'landlordContact'].map((key) => (
                    <li key={key}>{t(`${RR}.overview.requirements.${key}`)}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rr-warning-box">
              <AlertTriangle size={20} />
              <div>
                <strong>{t(`${RR}.overview.disclaimerTitle`)}</strong>
                <p>{t(`${RR}.overview.disclaimerText`)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === 'providers' && (
          <div className="rr-providers">
            <div className="rr-section">
              <h2 className="rr-section-title">{t(`${RR}.providers.sectionTitle`)}</h2>
              <p className="rr-section-text">{t(`${RR}.providers.sectionText`)}</p>
            </div>

            <div className="rr-provider-grid">
              {RENT_REPORTING_PROVIDERS.map((provider) => (
                <div key={provider.id} className="rr-provider-card glass">
                  <div className="rr-provider-header">
                    <div className="rr-provider-logo">{provider.logo_placeholder}</div>
                    <div className="rr-provider-name-section">
                      <h3 className="rr-provider-name">{provider.provider_name}</h3>
                      <div className="rr-bureau-badges">
                        {provider.bureaus_reported.map(b => <BureauBadge key={b} bureau={b} />)}
                      </div>
                    </div>
                  </div>

                  <div className="rr-provider-best-for">
                    <Star size={14} />
                    <span>
                      {t(`${RR}.providers.bestForPrefix`)}{' '}
                      {t(`${RR}.providers.items.${provider.id}.bestFor`, null, provider.best_for)}
                    </span>
                  </div>

                  <div className="rr-provider-details">
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.pastRentReporting`)}</span>
                      <span className="rr-detail-value">
                        {t(`${RR}.providers.labels.upToMonths`, { count: provider.past_rent_reporting_limit_months })}
                      </span>
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.futureRentReporting`)}</span>
                      <span className="rr-detail-value">
                        {provider.reports_future_rent ? t(`${RR}.providers.labels.yes`) : t(`${RR}.providers.labels.no`)}
                      </span>
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.setupFee`)}</span>
                      <span className="rr-detail-value highlight">${provider.setup_fee}</span>
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.monthlyFee`)}</span>
                      <span className="rr-detail-value highlight">
                        {t(`${RR}.providers.labels.perMonth`, { amount: `$${provider.monthly_fee}` })}
                      </span>
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.pastRentFee`)}</span>
                      <span className="rr-detail-value highlight">${provider.past_rent_reporting_fee}</span>
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.landlordRequired`)}</span>
                      <VerificationBadge value={provider.landlord_required} t={t} RR={RR} />
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.leaseVerification`)}</span>
                      <VerificationBadge value={provider.lease_verification_required ? 'yes' : 'no'} t={t} RR={RR} />
                    </div>
                    <div className="rr-detail-row">
                      <span className="rr-detail-label">{t(`${RR}.providers.labels.paymentProof`)}</span>
                      <VerificationBadge value={provider.payment_proof_required ? 'yes' : 'no'} t={t} RR={RR} />
                    </div>
                  </div>

                  <div className="rr-provider-watchout">
                    <HelpCircle size={14} />
                    <span>{t(`${RR}.providers.items.${provider.id}.watchOuts`, null, provider.watch_outs)}</span>
                  </div>

                  <div className="rr-provider-actions">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => selectProviderForCalc(provider)}
                      className="rr-calc-btn"
                    >
                      <Calculator size={14} /> {t(`${RR}.providers.useInCalculator`)}
                    </Button>
                    <a 
                      href={provider.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="rr-website-btn"
                    >
                      <ExternalLink size={14} /> {t(`${RR}.providers.visitWebsite`)}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <div className="rr-comparison-section">
              <h3 className="rr-subsection-title">{t(`${RR}.providers.quickComparison`)}</h3>
              <div className="rr-table-wrapper glass">
                <table className="rr-comparison-table">
                  <thead>
                    <tr>
                      <th>{t(`${RR}.providers.comparison.feature`)}</th>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <th key={p.id}>{p.provider_name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.bureaus`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>{t(`${RR}.providers.comparison.bureausCount`, { count: p.bureaus_reported.length })}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.pastRentMax`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>{t(`${RR}.providers.comparison.months`, { count: p.past_rent_reporting_limit_months })}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.setupFee`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>${p.setup_fee}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.monthlyFee`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>{t(`${RR}.providers.labels.perMonth`, { amount: `$${p.monthly_fee}` })}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.pastRentFee`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>${p.past_rent_reporting_fee}</td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.firstYearCost`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id} className="highlight">
                          ${p.setup_fee + p.past_rent_reporting_fee + (p.monthly_fee * 12)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td>{t(`${RR}.providers.comparison.landlordRequired`)}</td>
                      {RENT_REPORTING_PROVIDERS.map(p => (
                        <td key={p.id}>
                          {p.landlord_required === 'yes' ? <Check size={16} className="text-emerald" /> : 
                           p.landlord_required === 'no' ? <X size={16} className="text-slate" /> : 
                           <span className="text-amber">{t(`${RR}.providers.comparison.varies`)}</span>}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="rr-table-note">
                <Info size={14} /> {t(`${RR}.providers.tableNote`)}
              </p>
            </div>
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="rr-calculator">
            <div className="rr-section">
              <h2 className="rr-section-title">{t(`${RR}.calculator.title`)}</h2>
              <p className="rr-section-text">{t(`${RR}.calculator.subtitle`)}</p>
            </div>

            {selectedProvider && (
              <div className="rr-selected-provider glass">
                <span>
                  {t(`${RR}.calculator.usingRatesFrom`)}{' '}
                  <strong>{selectedProvider.provider_name}</strong>
                </span>
                <button onClick={() => setSelectedProvider(null)} className="rr-clear-btn">
                  <X size={14} /> {t(`${RR}.calculator.clear`)}
                </button>
              </div>
            )}

            <div className="rr-calc-grid">
              <div className="rr-calc-inputs glass">
                <h3 className="rr-calc-title">{t(`${RR}.calculator.yourInformation`)}</h3>
                
                <div className="rr-input-group">
                  <label>{t(`${RR}.calculator.monthlyRent`)}</label>
                  <div className="rr-input-wrapper">
                    <DollarSign size={16} />
                    <input
                      type="number"
                      value={calcInputs.monthlyRent}
                      onChange={(e) => handleInputChange('monthlyRent', e.target.value)}
                      min="0"
                      step="50"
                    />
                  </div>
                </div>

                <div className="rr-input-group">
                  <label>{t(`${RR}.calculator.monthsPastRent`)}</label>
                  <div className="rr-input-wrapper">
                    <Calendar size={16} />
                    <input
                      type="number"
                      value={calcInputs.monthsOfPastRent}
                      onChange={(e) => handleInputChange('monthsOfPastRent', e.target.value)}
                      min="0"
                      max="60"
                    />
                  </div>
                </div>

                <h3 className="rr-calc-title mt-4">{t(`${RR}.calculator.providerRates`)}</h3>

                <div className="rr-input-group">
                  <label>{t(`${RR}.calculator.providerMaxMonths`)}</label>
                  <input
                    type="number"
                    value={calcInputs.providerMaxMonths}
                    onChange={(e) => handleInputChange('providerMaxMonths', e.target.value)}
                    min="0"
                    max="36"
                  />
                </div>

                <div className="rr-input-row">
                  <div className="rr-input-group half">
                    <label>{t(`${RR}.calculator.setupFee`)}</label>
                    <div className="rr-input-wrapper">
                      <DollarSign size={16} />
                      <input
                        type="number"
                        value={calcInputs.setupFee}
                        onChange={(e) => handleInputChange('setupFee', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="rr-input-group half">
                    <label>{t(`${RR}.calculator.monthlyFee`)}</label>
                    <div className="rr-input-wrapper">
                      <DollarSign size={16} />
                      <input
                        type="number"
                        value={calcInputs.monthlyFee}
                        onChange={(e) => handleInputChange('monthlyFee', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="rr-input-row">
                  <div className="rr-input-group half">
                    <label>{t(`${RR}.calculator.pastRentFee`)}</label>
                    <div className="rr-input-wrapper">
                      <DollarSign size={16} />
                      <input
                        type="number"
                        value={calcInputs.pastRentFee}
                        onChange={(e) => handleInputChange('pastRentFee', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="rr-input-group half">
                    <label>{t(`${RR}.calculator.bureausReported`)}</label>
                    <select
                      value={calcInputs.numBureaus}
                      onChange={(e) => handleInputChange('numBureaus', e.target.value)}
                    >
                      {[1, 2, 3].map((n) => (
                        <option key={n} value={n}>{t(`${RR}.calculator.bureauOptions.${n}`)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <h3 className="rr-calc-title mt-4">{t(`${RR}.calculator.yourReadiness`)}</h3>

                <div className="rr-checkbox-group">
                  <label className="rr-checkbox">
                    <input
                      type="checkbox"
                      checked={calcInputs.hasPaymentProof}
                      onChange={(e) => handleInputChange('hasPaymentProof', e.target.checked)}
                    />
                    <span>{t(`${RR}.calculator.hasPaymentProof`)}</span>
                  </label>
                </div>

                <div className="rr-checkbox-group">
                  <label className="rr-checkbox">
                    <input
                      type="checkbox"
                      checked={calcInputs.hasLandlordVerification}
                      onChange={(e) => handleInputChange('hasLandlordVerification', e.target.checked)}
                    />
                    <span>{t(`${RR}.calculator.landlordCanVerify`)}</span>
                  </label>
                </div>
              </div>

              <div className="rr-calc-results glass">
                <h3 className="rr-calc-title">{t(`${RR}.calculator.resultsTitle`)}</h3>

                <div className="rr-result-fit">
                  <FitBadge result={calcResults.fitResult} t={t} RR={RR} />
                  <p className="rr-fit-desc">{t(`${RR}.calculator.fit.${calcResults.fitDescKey}`)}</p>
                </div>

                <div className="rr-results-grid">
                  <div className="rr-result-card">
                    <span className="rr-result-label">{t(`${RR}.calculator.eligiblePastMonths`)}</span>
                    <span className="rr-result-value">{calcResults.eligiblePastMonths}</span>
                  </div>
                  <div className="rr-result-card">
                    <span className="rr-result-label">{t(`${RR}.calculator.reportableRentVolume`)}</span>
                    <span className="rr-result-value">${calcResults.potentialReportedRent.toLocaleString()}</span>
                  </div>
                  <div className="rr-result-card highlight">
                    <span className="rr-result-label">{t(`${RR}.calculator.firstYearCost`)}</span>
                    <span className="rr-result-value">${calcResults.firstYearCost.toLocaleString()}</span>
                  </div>
                  <div className="rr-result-card">
                    <span className="rr-result-label">{t(`${RR}.calculator.costPerMonth`)}</span>
                    <span className="rr-result-value">${calcResults.costPerReportedMonth}</span>
                  </div>
                  <div className="rr-result-card">
                    <span className="rr-result-label">{t(`${RR}.calculator.bureauCoverage`)}</span>
                    <span className={`rr-result-value ${calcResults.bureauStrength}`}>
                      {t(`${RR}.calculator.bureauStrength.${calcResults.bureauStrength}`)}
                    </span>
                  </div>
                  <div className="rr-result-card">
                    <span className="rr-result-label">{t(`${RR}.calculator.readinessScore`)}</span>
                    <span className="rr-result-value">{calcResults.readinessScore}/100</span>
                  </div>
                </div>

                {calcResults.readinessIssues.length > 0 && (
                  <div className="rr-issues-box">
                    <h4><AlertTriangle size={16} /> {t(`${RR}.calculator.itemsToAddress`)}</h4>
                    <ul>
                      {calcResults.readinessIssues.map((issueKey, i) => (
                        <li key={i}>{t(`${RR}.calculator.issues.${issueKey}`)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div className="rr-checklist">
            <div className="rr-section">
              <h2 className="rr-section-title">{t(`${RR}.checklist.title`)}</h2>
              <p className="rr-section-text">{t(`${RR}.checklist.subtitle`)}</p>
            </div>

            <div className="rr-checklist-grid">
              {[
                { icon: Users, titleKey: 'personalInfo', items: ['legalName', 'address', 'moveIn', 'rentAmount'], itemPrefix: 'personal' },
                { icon: FileText, titleKey: 'documentation', items: ['lease', 'bankStatements', 'portalRecords', 'receipts'], itemPrefix: 'docs' },
                { icon: Building2, titleKey: 'landlordInfo', items: ['name', 'contact', 'permission', 'amounts'], itemPrefix: 'landlord' },
                { icon: Shield, titleKey: 'providerVerification', items: ['bureaus', 'latePayments', 'pastRentFees', 'pricing'], itemPrefix: 'provider' },
              ].map(({ icon: Icon, titleKey, items, itemPrefix }) => (
                <div key={titleKey} className="rr-checklist-card glass">
                  <h3><Icon size={20} /> {t(`${RR}.checklist.${titleKey}`)}</h3>
                  <ul className="rr-check-items">
                    {items.map((itemKey) => (
                      <li key={itemKey}>
                        <CheckCircle size={16} /> {t(`${RR}.checklist.${itemPrefix}.${itemKey}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="rr-warning-box critical">
              <AlertTriangle size={20} />
              <div>
                <strong>{t(`${RR}.checklist.criticalTitle`)}</strong>
                <ul className="rr-warning-list">
                  {['coverage', 'verification', 'fees', 'pastLimit', 'negativePolicy'].map((key) => (
                    <li key={key}>{t(`${RR}.checklist.critical.${key}`)}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rr-action-plan">
              <h3 className="rr-subsection-title">{t(`${RR}.checklist.actionPlanTitle`)}</h3>
              <div className="rr-steps">
                {['research', 'gather', 'verify', 'enroll', 'monitor'].map((stepKey, idx) => (
                  <div key={stepKey} className="rr-step glass">
                    <div className="rr-step-num">{idx + 1}</div>
                    <div className="rr-step-content">
                      <h4>{t(`${RR}.checklist.steps.${stepKey}.title`)}</h4>
                      <p>{t(`${RR}.checklist.steps.${stepKey}.text`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Disclaimer */}
      <div className="rr-footer-disclaimer">
        <p>
          <strong>{t(`${RR}.footer.disclaimer`)}</strong> {t(`${RR}.footer.text`)}
        </p>
      </div>
    </div>
  );
};

export default RentReportingBlueprint;
